import type { Dispatch } from 'react';
import { useCallback } from 'react';

import { ConfigureStepActions, configureStepDefaultState } from '../configure';
import { DetailsStepActions, detailsStepDefaultState } from '../details';
import { OnlineDetailsStepActions, onlineDetailsStepDefaultState } from '../online-details';
import { RequirementsStepActions, requirementsStepDefaultState } from '../requirements';
import { RewardsStepActions, rewardsStepDefaultState } from '../rewards';

import { deepMergeFilters, mergeRequirement } from './merge-utils';

import type { InitialWizardState } from '../../../types';
import type { ConfigureStepAction } from '../configure';
import type { DetailsStepAction } from '../details';
import type { OnlineDetailsStepAction } from '../online-details';
import type { Requirement, RequirementsStepAction } from '../requirements';
import type { RewardsStepAction } from '../rewards';

/**
 * Applies initial configure step state by dispatching INITIALIZE action with merged defaults
 */
export const applyInitialConfigureStep = (
  partialState: InitialWizardState['configureStep'] | undefined,
  dispatch: Dispatch<ConfigureStepAction>
) => {
  if (!partialState) {
    return;
  }

  dispatch({
    payload: {
      ...configureStepDefaultState,
      ...partialState,
    },
    type: ConfigureStepActions.INITIALIZE,
  });
};

/**
 * Applies initial details step state by dispatching INITIALIZE action with merged defaults
 */
export const applyInitialDetailsStep = (
  partialState: InitialWizardState['detailsStep'] | undefined,
  dispatch: Dispatch<DetailsStepAction>
) => {
  if (!partialState) {
    return;
  }

  dispatch({
    payload: {
      ...detailsStepDefaultState,
      ...partialState,
    },
    type: DetailsStepActions.INITIALIZE,
  });
};

/**
 * Applies initial requirements step state with deep merge for filters and numbered requirements
 */
export const applyInitialRequirementsStep = (
  partialState: InitialWizardState['requirementsStep'] | undefined,
  dispatch: Dispatch<RequirementsStepAction>
) => {
  if (!partialState) {
    return;
  }

  // Build the merged requirements state
  const mergedState = { ...requirementsStepDefaultState };

  // Apply meta properties (visited, activeFilter) if they exist
  if (partialState.visited !== undefined) {
    mergedState.visited = partialState.visited;
  }
  if (partialState.activeFilter !== undefined) {
    mergedState.activeFilter = partialState.activeFilter;
  }

  // Apply numbered requirements
  Object.entries(partialState).forEach(([key, partialRequirement]) => {
    // Skip meta properties - they're handled above
    if (key === 'activeFilter' || key === 'visited') {
      return;
    }

    const index = Number(key);
    const defaultReq = requirementsStepDefaultState[index] || requirementsStepDefaultState[1];

    // Type guard: after filtering out meta properties, partialRequirement should be a Requirement
    if (typeof partialRequirement === 'object' && partialRequirement !== null) {
      mergedState[index] = mergeRequirement(defaultReq, partialRequirement as Partial<Requirement>);
    }
  });

  dispatch({
    payload: mergedState,
    type: RequirementsStepActions.INITIALIZE,
  });
};

/**
 * Applies initial rewards step state with deep merge for condition filters
 */
export const applyInitialRewardsStep = (
  partialState: InitialWizardState['rewardsStep'] | undefined,
  dispatch: Dispatch<RewardsStepAction>
) => {
  if (!partialState) {
    return;
  }

  // Deep merge condition filters
  const mergedConditionFilters = deepMergeFilters(
    rewardsStepDefaultState.condition.filters,
    partialState.condition?.filters
  );

  dispatch({
    payload: {
      ...rewardsStepDefaultState,
      ...partialState,
      condition: {
        ...rewardsStepDefaultState.condition,
        ...(partialState.condition || {}),
        filters: mergedConditionFilters,
      },
    },
    type: RewardsStepActions.INITIALIZE,
  });
};

/**
 * Applies initial online details step state by dispatching INITIALIZE action with merged defaults
 */
export const applyInitialOnlineDetailsStep = (
  partialState: InitialWizardState['onlineDetailsStep'] | undefined,
  dispatch: Dispatch<OnlineDetailsStepAction>
) => {
  if (!partialState) {
    return;
  }

  dispatch({
    payload: {
      ...onlineDetailsStepDefaultState,
      ...partialState,
    },
    type: OnlineDetailsStepActions.INITIALIZE,
  });
};

/**
 * Creates a dispatch function wrapper that optionally triggers a side effect after dispatching
 */
export const useDispatchWithSideEffect = <T>(dispatch: Dispatch<T>, sideEffect: () => void) =>
  useCallback<(action: T, skipSideEffect?: boolean) => void>(
    (action: T, skipSideEffect = false) => {
      dispatch(action);
      if (!skipSideEffect) {
        sideEffect();
      }
    },
    [dispatch, sideEffect]
  );
